import React from "react";

import { useLocalization } from "@fluent/react";
import { Button, Chip, Row } from "@narmi/design_system";

import { getConfidenceChip } from "../../confidence";
import { formatReceiptAmount, formatReceiptDate } from "../../format";

import styles from "./ReceiptDetailsDrawer.module.scss";

import type { ReceiptSuggestion } from "../../hooks/useReceipt";

interface SuggestedMatchCardProps {
  suggestion: ReceiptSuggestion;
  variant?: "suggested" | "matched";
  onViewTransactionClick?: () => void;
  isViewTransactionPending?: boolean;
}

const SuggestedMatchCard = ({
  suggestion,
  variant = "suggested",
  onViewTransactionClick,
  isViewTransactionPending = false,
}: SuggestedMatchCardProps) => {
  const { l10n } = useLocalization();
  const txn = suggestion.candidate_transaction;
  // Null on a ledger attach — there's no matcher score to report, not a low one.
  const confidence =
    suggestion.confidence_score === null
      ? null
      : getConfidenceChip(suggestion.confidence_score);
  const titleKey =
    variant === "matched"
      ? "receipts-drawer-matched-transaction"
      : "receipts-drawer-suggested-match";
  const emptyStateKey =
    variant === "matched"
      ? "receipts-drawer-matched-transaction-empty"
      : "receipts-drawer-suggested-transaction-empty";

  return (
    <div
      className={`${styles.card} border--all rounded--all--m padding--all--m`}
      data-testid="suggested-match-card"
    >
      <Row justifyContent="space-between" alignItems="center">
        <Row.Item>
          <p
            className={`${styles["section-label"]} fontColor--secondary fontWeight--bold fontSize--xs`}
          >
            {l10n.getString(titleKey)}
          </p>
        </Row.Item>
        {onViewTransactionClick && (
          <Row.Item shrink>
            <Button
              kind="plain"
              size="xs"
              endIcon="arrow-right"
              label={l10n.getString("receipts-drawer-view-transaction")}
              onClick={onViewTransactionClick}
              disabled={isViewTransactionPending}
              testId="suggested-match-card-view-transaction"
            />
          </Row.Item>
        )}
      </Row>
      <div className="margin--top--s">
        {txn ? (
          <>
            <Row justifyContent="space-between" alignItems="center">
              <Row.Item>
                <div className="fontWeight--semibold">
                  {txn.description || txn.merchant}
                </div>
              </Row.Item>
              <Row.Item shrink>
                <div className="fontWeight--semibold">
                  {formatReceiptAmount(txn.amount)}
                </div>
              </Row.Item>
            </Row>
            <Row justifyContent="space-between" alignItems="center">
              <Row.Item>
                <div className="fontColor--secondary fontSize--s">
                  {formatReceiptDate(txn.datetime)}
                  {txn.description && txn.merchant && ` · ${txn.merchant}`}
                </div>
              </Row.Item>
              {confidence && (
                <Row.Item shrink>
                  <Chip
                    kind={confidence.kind}
                    label={l10n.getString(confidence.labelKey)}
                  />
                </Row.Item>
              )}
            </Row>
          </>
        ) : (
          <p
            className="fontColor--secondary"
            data-testid="suggested-match-card-empty"
          >
            {l10n.getString(emptyStateKey)}
          </p>
        )}
      </div>
    </div>
  );
};

export default SuggestedMatchCard;
